import React from 'react'
import {
  getRecareKpi,
  type RecareKpiData,
} from '@pages/Dashboard/RecareDetail/recareDetail.mockData'
import {
  getRecareDonutConfig,
  type HalfDonutConfig,
} from './dashboardOverview.mockData'
import { getOverviewRecareTopMetrics } from './overviewMetrics.mockData'
import OverviewMetricSectionCardContent from './OverviewMetricSectionCardContent.component'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DiagnosisOverviewResult } from '@services/api/dashboard/diagnosis.api'
import type { RecareOverviewResult } from '@services/api/dashboard/recare.api'
import type { TreatmentAcceptanceOverviewResult } from '@services/api/dashboard/treatment-acceptance-overview.api'
import { buildDiagnosisOverviewSection } from '@utils/diagnosisOverview.util'
import { buildTreatmentAcceptanceOverviewSection } from '@utils/treatmentAcceptanceOverview.util'
import {
  buildRecareDonutFromOverview,
  getRecareRateGoalLabel,
  mapRecareOverviewToTopMetrics,
} from '@utils/recareOverview.util'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import type { DashboardSectionCardSkeletonProps } from '@components/pages/Dashboard/DashboardSectionCardSkeleton.component'

const INSURANCE_PATIENT_LEGEND = [
  { label: 'Insurance', color: 'var(--blue-400)' },
  { label: 'Patient', color: 'var(--orange-300)' },
] as const

const RECARE_LEGEND = [
  { label: 'Recare rate', color: 'var(--blue-400)' },
  { label: 'Remaining', color: 'var(--gray-200)' },
] as const

const EMPTY_RECARE_KPI: RecareKpiData = {
  recareRate: '0%',
  patientsDue: '0',
  scheduled: '0',
  scheduledOver: '0',
  overdue: '0',
  unscheduledValue: '$0',
  unscheduledCount: 0,
  comparisonLabel: '',
  recareRateDelta: '0%',
  recareRateTrend: 'up',
  patientsDueDelta: '0',
  patientsDueTrend: 'up',
  scheduledDelta: '0',
  scheduledTrend: 'up',
  overdueDelta: '0',
  overdueTrend: 'up',
}

const EMPTY_RECARE_DONUT: HalfDonutConfig = {
  primaryValue: 0,
  secondaryValue: 100,
  slices: [
    { name: 'Recare rate', value: 0, color: 'var(--blue-400)' },
    { name: 'Remaining', value: 100, color: 'var(--gray-200)' },
  ],
  valueFormatter: (val: number): string => `${Math.round(val)}%`,
}

function vsHighlight(actual: string, goal: string): React.ReactElement {
  return (
    <>
      {actual} <span className="dashboard-metric-value-vs">vs</span>{' '}
      <span className="dashboard-metric-value-over">{goal}</span>
    </>
  )
}

function wrapOverviewSectionCard(
  content: React.ReactElement,
  options: {
    title: string
    titleHref?: string
    className?: string
    disableTitleLink: boolean
    isEmpty?: boolean
    loading?: boolean
    skeleton?: DashboardSectionCardSkeletonProps
  },
): React.ReactElement {
  if (!options.titleHref) return content
  return (
    <DashboardSectionCard
      title={options.title}
      titleHref={options.titleHref}
      className={options.className}
      loading={options.loading}
      skeleton={options.skeleton}
      isEmpty={options.isEmpty}
      disableTitleLink={options.disableTitleLink}
    >
      {content}
    </DashboardSectionCard>
  )
}

const OVERVIEW_METRIC_CHART_SKELETON: DashboardSectionCardSkeletonProps = {
  metrics: 4,
  block: 'chart',
}

export function OverviewRecareSectionContent({
  range,
  title = 'Recare',
  titleHref,
  className = 'dashboard-recare-card dashboard-acceptance-card',
  recareOverview,
  loading,
}: {
  range: RangeKey
  title?: string
  titleHref?: string
  className?: string
  recareOverview: RecareOverviewResult | null
  loading: boolean
}): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const kpi =
    recareOverview?.kpis ?? (useMocks ? getRecareKpi(range) : EMPTY_RECARE_KPI)
  // Effective rendered KPI (response, mock, or empty fallback).
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      kpi.recareRate,
      kpi.patientsDue,
      kpi.scheduled,
      kpi.overdue,
      kpi.unscheduledValue,
      kpi.unscheduledCount,
    ),
  )
  const topMetrics = recareOverview?.kpis
    ? mapRecareOverviewToTopMetrics(recareOverview.kpis)
    : withDashboardMetricShellValues(
        getOverviewRecareTopMetrics(range),
        useMocks,
      )
  const chart =
    buildRecareDonutFromOverview(recareOverview) ??
    (useMocks ? getRecareDonutConfig(range) : EMPTY_RECARE_DONUT)
  const goalLabel = getRecareRateGoalLabel(recareOverview)
  const recareHasPayload = recareOverview != null
  const recarePending = isDashboardSectionPending(loading, recareHasPayload)
  return wrapOverviewSectionCard(
    <OverviewMetricSectionCardContent
      topMetrics={topMetrics}
      highlight={{
        leftLabel: 'Recare rate',
        value: vsHighlight(kpi.recareRate, goalLabel),
      }}
      chart={{
        title: 'Recare rate',
        primaryValue: chart.primaryValue,
        secondaryValue: chart.secondaryValue,
        valueFormatter: chart.valueFormatter,
        slices: chart.slices,
      }}
      legend={[...RECARE_LEGEND]}
    />,
    {
      title,
      titleHref,
      className,
      disableTitleLink: isEmpty,
      isEmpty,
      loading: recarePending,
      skeleton: OVERVIEW_METRIC_CHART_SKELETON,
    },
  )
}

export function OverviewDiagnosisSectionContent({
  range,
  title = 'Diagnosis',
  titleHref,
  className = 'dashboard-diagnosis-card dashboard-acceptance-card',
  data,
  loading,
}: {
  range: RangeKey
  title?: string
  titleHref?: string
  className?: string
  data: DiagnosisOverviewResult | null
  loading: boolean
}): React.ReactElement {
  const { topMetrics, captureRate, captureGoal, chart } =
    buildDiagnosisOverviewSection(range, data?.kpis)
  // Effective rendered data: top metrics plus raw chart values.
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      ...topMetrics.map((metric) => metric.value),
      chart.primaryValue,
      chart.secondaryValue,
      captureRate,
    ),
  )
  const diagnosisPending = isDashboardSectionPending(loading, data != null)
  return wrapOverviewSectionCard(
    <OverviewMetricSectionCardContent
      topMetrics={topMetrics}
      highlight={{
        leftLabel: 'Diagnosis capture',
        value: vsHighlight(`${captureRate}%`, captureGoal),
      }}
      chart={{
        title: 'Treatment value',
        primaryValue: chart.primaryValue,
        secondaryValue: chart.secondaryValue,
        valueFormatter: chart.valueFormatter,
        slices: chart.slices,
      }}
      legend={[...INSURANCE_PATIENT_LEGEND]}
    />,
    {
      title,
      titleHref,
      className,
      disableTitleLink: isEmpty,
      isEmpty,
      loading: diagnosisPending,
      skeleton: OVERVIEW_METRIC_CHART_SKELETON,
    },
  )
}

export function OverviewTreatmentAcceptanceSectionContent({
  range,
  title = 'Treatment acceptance',
  titleHref,
  className = 'dashboard-acceptance-card',
  data,
  loading,
}: {
  range: RangeKey
  title?: string
  titleHref?: string
  className?: string
  data: TreatmentAcceptanceOverviewResult | null
  loading: boolean
}): React.ReactElement {
  const { topMetrics, extras, chart } = buildTreatmentAcceptanceOverviewSection(
    range,
    data?.kpis,
  )
  // Effective rendered data: top metrics, completed count, raw chart values.
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      ...topMetrics.map((metric) => metric.value),
      extras.txCompletedActual,
      chart.primaryValue,
      chart.secondaryValue,
    ),
  )
  const treatmentPending = isDashboardSectionPending(loading, data != null)
  return wrapOverviewSectionCard(
    <OverviewMetricSectionCardContent
      topMetrics={topMetrics}
      highlight={{
        leftLabel: 'Tx completed',
        value: (
          <>
            {extras.txCompletedActual}
            {' / '}
            <span className="dashboard-metric-value-over">
              {extras.txCompletedTotal}
            </span>
          </>
        ),
      }}
      chart={{
        title: 'Insurance vs patient split',
        primaryValue: chart.primaryValue,
        secondaryValue: chart.secondaryValue,
        valueFormatter: chart.valueFormatter,
        slices: chart.slices,
      }}
      legend={[...INSURANCE_PATIENT_LEGEND]}
    />,
    {
      title,
      titleHref,
      className,
      disableTitleLink: isEmpty,
      isEmpty,
      loading: treatmentPending,
      skeleton: OVERVIEW_METRIC_CHART_SKELETON,
    },
  )
}
